/**
 * 분석 통계 API 서비스
 *
 * AICC 운영 현황을 파악하기 위한 분석/통계 API.
 * 대시보드 요약, 사용 추이, 모델별 토큰 소비량, 테넌트 순위,
 * 채널별 상담 통계, 감사 로그 조회 등 운영 의사결정에 필요한 데이터를 제공.
 */

import { API_BASE_URL } from '../../constants/api';
import { apiRequest } from './apiClient';

/**
 * Analytics API Class
 */
export class AnalyticsAPI {
  /**
   * AnalyticsAPI constructor
   *
   * @param {Object} config - Configuration object
   * @param {string} config.baseUrl - API base URL (default: API_BASE_URL)
   * @param {Object} config.endpoints - Endpoint paths
   * @param {Object} config.logger - Logger object (default: console)
   */
  constructor({
    baseUrl = API_BASE_URL,
    endpoints = {
      analytics: '/api/analytics',
      auditLogs: '/api/analytics/auditLog',
    },
    logger = console,
  } = {}) {
    this.baseUrl = baseUrl;
    this.endpoints = endpoints;
    this.logger = logger;
  }

  /**
   * Build query string from parameters
   */
  _buildQueryString(params) {
    const filtered = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    return filtered.length > 0 ? `?${filtered.join('&')}` : '';
  }

  // ==================== Overview APIs ====================

  /**
   * Get dashboard overview statistics
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<OverviewResponse>}
   */
  async getOverview(tenantId = null) {
    const query = this._buildQueryString({ tenantId });
    const url = `${this.baseUrl}${this.endpoints.analytics}/overview${query}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== Usage Trend APIs ====================

  /**
   * Get usage trend data for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<UsageTrendResponse>}
   */
  async getUsageTrend(startDate, endDate, tenantId = null) {
    const query = this._buildQueryString({ startDate, endDate, tenantId });
    const url = `${this.baseUrl}${this.endpoints.analytics}/usage-trend${query}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== Token Usage APIs ====================

  /**
   * Get token usage statistics by model
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<TokenUsageResponse>}
   */
  async getTokenUsage(startDate, endDate, tenantId = null) {
    const query = this._buildQueryString({ startDate, endDate, tenantId });
    const url = `${this.baseUrl}${this.endpoints.analytics}/token-usage${query}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== Tenant Ranking APIs ====================

  /**
   * Get tenant ranking by metric
   * @param {Object} params - Parameters
   * @param {string} params.metric - Ranking metric ('conversations' or 'tokens')
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {number} [params.limit=10] - Maximum number of results
   * @returns {Promise<TenantRankingResponse>}
   */
  async getTenantRanking({ metric = 'conversations', startDate, endDate, limit = 10 }) {
    const query = this._buildQueryString({ metric, startDate, endDate, limit });
    const url = `${this.baseUrl}${this.endpoints.analytics}/tenant-ranking${query}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== Channel Analytics APIs ====================

  /**
   * Get channel usage statistics
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<ChannelUsageResponse>}
   */
  async getChannelUsage(startDate, endDate, tenantId = null) {
    const query = this._buildQueryString({ tenantId, startDate, endDate });
    const url = `${this.baseUrl}${this.endpoints.analytics}/channels${query}`;
    return apiRequest(url, {}, this.logger);
  }

  /**
   * Get channel detail statistics
   * @param {string} channelId - Channel ID
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<ChannelDetailResponse>}
   */
  async getChannelDetail(channelId, startDate, endDate) {
    const query = this._buildQueryString({ startDate, endDate });
    const url = `${this.baseUrl}${this.endpoints.analytics}/channels/${channelId}/detail${query}`;
    return apiRequest(url, {}, this.logger);
  }

  /**
   * Get channel type distribution
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<ChannelTypeDistResponse>}
   */
  async getChannelTypeDistribution(startDate, endDate, tenantId = null) {
    const query = this._buildQueryString({ tenantId, startDate, endDate });
    const url = `${this.baseUrl}${this.endpoints.analytics}/channels/by-type${query}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== Aggregation APIs ====================

  /**
   * Trigger manual aggregation for a specific date
   * @param {string} date - Date to aggregate (YYYY-MM-DD)
   * @returns {Promise<{status: string, message: string}>}
   */
  async triggerAggregation(date) {
    const query = this._buildQueryString({ date });
    const url = `${this.baseUrl}${this.endpoints.analytics}/aggregate${query}`;
    return apiRequest(url, { method: 'POST' }, this.logger);
  }

  /**
   * Backfill statistics for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<{status: string, message: string}>}
   */
  async backfillStatistics(startDate, endDate) {
    const query = this._buildQueryString({ startDate, endDate });
    const url = `${this.baseUrl}${this.endpoints.analytics}/backfill${query}`;
    return apiRequest(url, { method: 'POST' }, this.logger);
  }

  // ==================== Audit Log APIs ====================
  // All audit log queries use ElastiCore standard POST /search endpoint

  /**
   * Search audit logs with filters (ElastiCore standard)
   * @param {Object} params - Search parameters
   * @param {string} [params.userId] - Filter by user ID
   * @param {string} [params.actionType] - Filter by action type (maps to 'action' in backend)
   * @param {string} [params.targetType] - Filter by target type (maps to 'resourceType' in backend)
   * @param {string} [params.targetId] - Filter by target ID (maps to 'resourceId' in backend)
   * @param {string} [params.startDate] - Start date (ISO datetime)
   * @param {string} [params.endDate] - End date (ISO datetime)
   * @param {number} [params.page=0] - Page number
   * @param {number} [params.size=20] - Page size
   * @returns {Promise<PagedAuditLogResponse>}
   */
  async searchAuditLogs({
    userId,
    actionType,
    targetType,
    targetId,
    startDate,
    endDate,
    page = 0,
    size = 20,
  } = {}) {
    const searchBody = {
      pageNumber: page,
      pageSize: size,
      sortColumn: 'timestamp',
      sortAscending: false, // 최신순 (시간 역순)
    };

    // Map frontend field names to backend field names
    if (userId) searchBody.userId = userId;
    if (actionType) searchBody.action = actionType; // Frontend: actionType -> Backend: action
    if (targetType) searchBody.resourceType = targetType; // Frontend: targetType -> Backend: resourceType
    if (targetId) searchBody.resourceId = targetId; // Frontend: targetId -> Backend: resourceId

    // Date range filters
    if (startDate) searchBody.timestampFrom = startDate;
    if (endDate) searchBody.timestampTo = endDate;

    const url = `${this.baseUrl}${this.endpoints.auditLogs}/search`;
    const response = await apiRequest(
      url,
      {
        method: 'POST',
        body: JSON.stringify(searchBody),
      },
      this.logger
    );

    // Map backend response fields to frontend expected fields
    if (response && response.content) {
      // Detect Java object toString() like "com.xsk.xyro...@68ef4de2"
      const isJavaRef = (v) => typeof v === 'string' && /^[\w.]+@[0-9a-f]+$/i.test(v);

      response.content = response.content.map((log) => {
        // Extract userEmail and message from details JSON (stored by AuditLoggingAspect)
        let userEmail = null;
        let message = null;
        if (log.details) {
          try {
            const parsed = JSON.parse(log.details);
            if (parsed) {
              if (parsed.userEmail && !isJavaRef(parsed.userEmail)) {
                userEmail = parsed.userEmail;
              }
              if (parsed.message && !isJavaRef(parsed.message)) {
                message = parsed.message;
              }
            }
          } catch (_) {
            /* details may not be JSON */
          }
        }
        const userId = isJavaRef(log.userId) ? null : log.userId;
        return {
          id: log.id,
          userId: userId,
          userEmail: userEmail || userId,
          actionType: log.action,
          targetType: log.resourceType,
          targetId: log.resourceId,
          description: message, // Extract from details JSON, null if absent
          details: log.details,
          ipAddress: log.ipAddress,
          createdAt: log.timestamp,
          tenantId: log.tenantId,
        };
      });
    }

    return response;
  }

  /**
   * Get audit logs for a specific user (ElastiCore standard)
   * @param {string} userId - User ID
   * @param {number} [page=0] - Page number
   * @param {number} [size=20] - Page size
   * @returns {Promise<PagedAuditLogResponse>}
   */
  async getLogsByUser(userId, page = 0, size = 20) {
    return this.searchAuditLogs({ userId, page, size });
  }

  /**
   * Get audit logs for a specific target (ElastiCore standard)
   * @param {string} targetType - Target type (TENANT, CHANNEL, USER, etc.)
   * @param {string} targetId - Target ID
   * @returns {Promise<AuditLogResponse[]>}
   */
  async getLogsForTarget(targetType, targetId) {
    const response = await this.searchAuditLogs({
      targetType,
      targetId,
      size: 1000, // Get all logs for the target
    });
    return response?.content || [];
  }

  /**
   * Get available action types for filtering
   * Hardcoded action types since backend doesn't provide this endpoint
   * @returns {Promise<string[]>}
   */
  async getActionTypes() {
    // Return hardcoded action types based on common audit actions
    return Promise.resolve([
      'LOGIN',
      'LOGOUT',
      'CREATE',
      'UPDATE',
      'DELETE',
      'STATE_CHANGE',
      'ENABLE',
      'DISABLE',
      'ESCALATION',
    ]);
  }

  /**
   * Get action type summary for a date range
   * Note: Backend doesn't provide summary endpoint, returns empty array
   * @param {string} startDate - Start date (ISO datetime)
   * @param {string} endDate - End date (ISO datetime)
   * @returns {Promise<{actionType: string, count: number}[]>}
   */
  async getActionTypeSummary(startDate, endDate) {
    // Backend doesn't have a summary endpoint, return empty array
    this.logger.warn('getActionTypeSummary: Backend does not support summary endpoint');
    return Promise.resolve([]);
  }

  // ==================== Escalation Analytics APIs ====================

  /**
   * Get escalation statistics for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<EscalationStatsResponse>}
   */
  async getEscalationStats(startDate, endDate, tenantId = null) {
    const query = this._buildQueryString({ startDate, endDate, tenantId });
    const url = `${this.baseUrl}/api/orchestrator/escalation-stats${query}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== Admin Analytics APIs ====================

  /**
   * Get cost analysis for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<CostAnalysisResponse>}
   */
  async getCostAnalysis(startDate, endDate, tenantId = null) {
    const query = this._buildQueryString({ startDate, endDate, tenantId });
    const url = `${this.baseUrl}/api/admin/analytics/cost${query}`;
    return apiRequest(url, {}, this.logger);
  }

  /**
   * Get active session statistics
   * @param {string} [tenantId] - Optional tenant ID filter
   * @returns {Promise<ActiveSessionStatsResponse>}
   */
  async getActiveSessionStats(tenantId = null) {
    const query = this._buildQueryString({ tenantId });
    const url = `${this.baseUrl}/api/admin/sessions/stats${query}`;
    return apiRequest(url, {}, this.logger);
  }
}

// Default instance
export const defaultAnalyticsAPI = new AnalyticsAPI();

// Convenience exports
export const getOverview = (tenantId) => defaultAnalyticsAPI.getOverview(tenantId);
export const getUsageTrend = (startDate, endDate, tenantId) =>
  defaultAnalyticsAPI.getUsageTrend(startDate, endDate, tenantId);
export const getTokenUsage = (startDate, endDate, tenantId) =>
  defaultAnalyticsAPI.getTokenUsage(startDate, endDate, tenantId);
export const getTenantRanking = (params) => defaultAnalyticsAPI.getTenantRanking(params);
export const getChannelUsage = (startDate, endDate, tenantId) =>
  defaultAnalyticsAPI.getChannelUsage(startDate, endDate, tenantId);
export const getChannelDetail = (channelId, startDate, endDate) =>
  defaultAnalyticsAPI.getChannelDetail(channelId, startDate, endDate);
export const getChannelTypeDistribution = (startDate, endDate, tenantId) =>
  defaultAnalyticsAPI.getChannelTypeDistribution(startDate, endDate, tenantId);
export const searchAuditLogs = (params) => defaultAnalyticsAPI.searchAuditLogs(params);
export const getActionTypes = () => defaultAnalyticsAPI.getActionTypes();
export const getCostAnalysis = (startDate, endDate, tenantId) =>
  defaultAnalyticsAPI.getCostAnalysis(startDate, endDate, tenantId);
export const getActiveSessionStats = (tenantId) =>
  defaultAnalyticsAPI.getActiveSessionStats(tenantId);
export const getEscalationStats = (startDate, endDate, tenantId) =>
  defaultAnalyticsAPI.getEscalationStats(startDate, endDate, tenantId);
